// Inline format marking note text that has an assigned task. The value is the
// internal_tasks id; it lives in the delta, so it moves with the text, syncs
// over y-quill and is saved with the chart JSON. No href — clicks are handled
// by the page (TaskLinkCard), never navigated.
//
// Tag collision: Quill's built-in `link` blot is also an <a>. Parchment looks
// blots up by class before tag, and a blot that has a className never claims
// the bare tag, so plain <a href> still parses as `link` (pinned in the test).
export const TASK_LINK_FORMAT = "task-link";
const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export function isTaskId(value) {
  return typeof value === "string" && UUID_RE.test(value);
}

let registered = false;

export function registerTaskLink(Quill) {
  if (registered) return;
  const Inline = Quill.import("blots/inline");
  class TaskLinkBlot extends Inline {
    static create(value) {
      const node = super.create();
      // Only a UUID ever reaches the DOM. A bad value leaves a node with no
      // data-task-id, which formats() reports as no format, so Quill unwraps it.
      // No title here: patients / read-only viewers must not get a tooltip
      // advertising a task they can't open. attachTaskLinkClicks adds it on
      // hover for interactive viewers only.
      if (isTaskId(value)) node.setAttribute("data-task-id", value);
      return node;
    }

    static formats(node) {
      const id = node.getAttribute("data-task-id");
      return isTaskId(id) ? id : undefined;
    }

    format(name, value) {
      if (name === TASK_LINK_FORMAT && value && isTaskId(value)) {
        this.domNode.setAttribute("data-task-id", value);
      } else {
        super.format(name, value);
      }
    }
  }
  TaskLinkBlot.blotName = TASK_LINK_FORMAT;
  TaskLinkBlot.tagName = "A";
  TaskLinkBlot.className = "ql-task-link";
  Quill.register(TaskLinkBlot, true);
  registered = true;
}

export function taskIdAt(node) {
  const el = node && (node.nodeType === 1 ? node : node.parentElement);
  const link = el && el.closest ? el.closest(".ql-task-link") : null;
  const id = link ? link.getAttribute("data-task-id") : null;
  return isTaskId(id) ? id : null;
}

// Class put on the Quill container for viewers who must not see or open task
// links (patients). DocumentBody's CSS renders links as plain text under it.
export const TASK_LINKS_INERT_CLASS = "ql-task-links-inert";
export const TASK_LINK_TOOLTIP = "Assigned task — click for details";

// Wires task-link clicks on a Quill editor. Task details are staff-only (the
// task endpoint has no per-user auth), so read-only/patient viewers get NO
// listener and an inert look: `interactive: false` only marks the container.
// onOpen({ taskId, rect }) gets rect relative to quill.container.parentNode,
// the page's portal target for the card. Returns a cleanup function.
export function attachTaskLinkClicks(quill, { interactive, onOpen }) {
  const container = quill && quill.container;
  if (!container || !quill.root) return () => {};
  if (container.classList) container.classList.toggle(TASK_LINKS_INERT_CLASS, !interactive);
  if (!interactive) return () => {};
  const onClick = (e) => {
    const id = taskIdAt(e.target);
    if (!id) return;
    e.preventDefault();
    const el = e.target.nodeType === 1 ? e.target : e.target.parentElement;
    const link = el && el.closest(".ql-task-link");
    const host = container.parentNode;
    if (!link || !host) return;
    const r = link.getBoundingClientRect();
    const h = host.getBoundingClientRect();
    onOpen({
      taskId: id,
      rect: { top: r.top - h.top, left: r.left - h.left, bottom: r.bottom - h.top, right: r.right - h.left },
    });
  };
  const onOver = (e) => {
    if (!taskIdAt(e.target)) return;
    const el = e.target.nodeType === 1 ? e.target : e.target.parentElement;
    const link = el && el.closest(".ql-task-link");
    if (link && !link.hasAttribute("title")) link.setAttribute("title", TASK_LINK_TOOLTIP);
  };
  quill.root.addEventListener("click", onClick);
  quill.root.addEventListener("mouseover", onOver);
  return () => {
    quill.root.removeEventListener("click", onClick);
    quill.root.removeEventListener("mouseover", onOver);
  };
}

// Link `range` to task `id`. Works in View mode too: the editor is disabled
// there and Quill drops 'user' changes while disabled, but only 'user' changes
// are autosaved (DocumentBody onChange), so enable it just for this one call.
export function applyTaskLink(quill, range, id) {
  if (!quill || !range || !(range.length > 0) || !isTaskId(id)) return false;
  const wasEnabled = quill.isEnabled();
  if (!wasEnabled) quill.enable(true);
  try {
    quill.formatText(range.index, range.length, TASK_LINK_FORMAT, id, "user");
  } finally {
    if (!wasEnabled) quill.enable(false);
  }
  return true;
}
